import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import CO2Service, { ShowCo2DataType } from '~/api/services/co2'
import Layout from '~/components/Layout'
import { handleErrors } from '~/utils/helpers'
import { StyledGrid, StyledSegment, CustomRow, StyledAccordion } from './style'
import { Accordion, Divider, Grid, Icon, Transition } from 'semantic-ui-react'
import { StyledGridColumns, StyledGridRow } from '../../MilkFixedPrice/ShowMilkFixedPrice/style'
import dayjs from 'dayjs'
import { RouteTypes } from '~/routes/constants'
import FarmersTable from './FarmersTable'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import { Nullable } from '~/types'
import { SURVEY_STATUS_KEYS } from '~/Constants'

const ShowCO2Climate: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()

  const [showData, setShowData] = useState<Nullable<ShowCo2DataType>>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: ''
  })
  const [activeIndex, setActiveIndex] = useState(-1)

  const {
    isLoading: isBusyData,
    data: farmerGroups,
    refetch: fetchFilteredData
  } = CO2ClimateQuery.getFarmerGroups(
    id,
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
    }
  );

  useEffect(() => {
    fetchShowData()
    fetchFilteredData()
  }, [t])

  async function fetchShowData() {
    setIsBusy(true)
    try {
      const response = await CO2Service.getCO2ShowData(id)
      setShowData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderShowData() {
    return <StyledSegment placeholder>
      <StyledGrid columns={2} stackable>
        <Divider vertical style={{ margin: 0 }} />
        <Grid.Column>
          <Grid columns={2} stackable>
            <CustomRow>
              <Grid.Column>{t('Title')}</Grid.Column>
              <StyledGridColumns>{showData?.title || '-'}</StyledGridColumns>
            </CustomRow>
            <StyledGridRow>
              <Grid.Column>{t('Status')}</Grid.Column>
              <StyledGridColumns>{showData?.statusLabel}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
        <Grid.Column>
          <Grid columns={2} stackable style={{ marginLeft: 5 }}>
            <StyledGridRow>
              <Grid.Column>{t('Start')}</Grid.Column>
              <StyledGridColumns>{dayjs(showData?.start).format(('DD.MM.YYYY HH:mm'))}</StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('End')}</Grid.Column>
              <StyledGridColumns>{dayjs(showData?.end).format(('DD.MM.YYYY HH:mm'))}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
      </StyledGrid>
    </StyledSegment>
  }

  function renderFarmers() {
    return <FarmersTable
      farmerGroupId={activeIndex >= 0 && rows.length > 0 ? rows[activeIndex]?.id : ''}
      isLive={showData?.status === SURVEY_STATUS_KEYS.live}
    />
  }

  function handleFarmerGroupClick(e, titleProps) {
    const { index } = titleProps
    setActiveIndex(activeIndex === index ? -1 : index);
  }

  function calculateCompleted(farmerGroup) {
    return `${farmerGroup?.completedFarmers} / ${farmerGroup.totalFarmers}`;
  }

  function renderFarmerGroups() {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {rows?.map((farmerGroup, index) => {
        const active = activeIndex === index
        return <StyledAccordion
          styled
          key={farmerGroup.id}
        >
          <Accordion.Title
            onClick={handleFarmerGroupClick}
            index={index}
            active={active}
          >
            <div>{`${farmerGroup.name} (${calculateCompleted(farmerGroup)})`}</div>
            <Icon name={active ? 'minus' : 'plus'} />
          </Accordion.Title>
          <Transition visible={active} animation='slide down' duration={200}>
            <Accordion.Content
              active={active}
            >
              {renderFarmers()}
            </Accordion.Content>
          </Transition>
        </StyledAccordion>
      })}
    </div>
  }
  const { rows } = farmerGroups ? farmerGroups : { rows: [] };

  return <Layout
    title={<span dangerouslySetInnerHTML={{ __html: t('Show CO2 climate check') }} />}
    segmented
    isCo2Loader={true}
    isBusy={isBusy || isBusyData}
    close={RouteTypes.CO2_CLIMATE}
  >
    {renderShowData()}
    {renderFarmerGroups()}
  </Layout>
}
export default ShowCO2Climate